import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  Product2AttributeSchema,
  Product2AttributeValueSchema,
  Product2AttributeValueUpsertSchema,
} from "../product2/product2-attribute-types";
import {
  Product2ImageSchema,
  Product2StatusSchema,
  Product2VariantSchema_,
} from "../product2/product2-types";
import { WarehouseSchema } from "../warehouse/warehouse-types";

/* InventoryTransferStatus */
export enum InventoryTransferStatus {
  PENDING = "pending",
  FULFILLED = "fulfilled",
  ARRIVED = "arrived",
  RECEIVED = "received",
}

export const InventoryTransferStatusSchema = createEnum([
  InventoryTransferStatus.PENDING,
  InventoryTransferStatus.FULFILLED,
  InventoryTransferStatus.ARRIVED,
  InventoryTransferStatus.RECEIVED,
]);

/* TransferItem */
export const TransferItemSchema = z.object({
  quantity: z.number(),
  variant: z
    .object({
      _id: z.string(),
      sku: z.string(),
      product: z.object({ name: z.string() }),
    })
    .nullable(),
});
export type TransferItem = z.infer<typeof TransferItemSchema>;

/* InventoryTransfer */
export const InventoryTransferSchema = z.object({
  _id: z.string(),
  fromWarehouse: z.object({ _id: z.string(), name: z.string() }),
  toWarehouse: z.object({ _id: z.string(), name: z.string() }),
  sentItems: z.array(TransferItemSchema),
  receivedItems: z.array(TransferItemSchema),
  status: InventoryTransferStatusSchema,
  verified: z.boolean(),
  notes: z.array(z.string()),
  createdAt: z.string(),
  team: z.object({ _id: z.string(), name: z.string() }),
  updatedAt: z.string(),
});
export type InventoryTransfer = z.infer<typeof InventoryTransferSchema>;

/**
 * Product2Variant
 *
 * NOTE: This schema was built assuming references have already been populated.
 * See @product2-types.ts for the schema that most accurately represents what is stored
 * on the Product2 collection in MongoDB. If possible, use the variant schema from @product2-types.ts
 * instead of this schema.
 *
 * TODO: Create a new schema in @product2-types.ts that represents the "populated"
 * version of a Product2Variant document.
 * */
export const Product2VariantSchema = Product2VariantSchema_.extend({
  images: z.any(),
  attributeValues: z.array(Product2AttributeValueSchema),
});

export type Product2Variant = z.infer<typeof Product2VariantSchema>;

const Product2VariantInputSchema_ = Product2VariantSchema.extend({
  _id: z.string().optional(),
  attributeValues: z.array(Product2AttributeValueUpsertSchema),
});

export const Product2VariantInputSchema: z.ZodType<Product2VariantInput> =
  Product2VariantInputSchema_;
export interface Product2VariantInput
  extends z.infer<typeof Product2VariantInputSchema_> {}

export type Product2VariantWithQuantity = Product2VariantInput & {
  quantity: number;
};

/**
 * Product2
 *
 * NOTE: This schema was built assuming references have already been populated.
 * See @product2-types.ts for the schema that most accurately represents what is stored
 * on the Product2 collection in MongoDB. If possible, use the schema from @product2-types.ts
 * instead of this schema.
 *
 * TODO: Create a new schema in @product2-types.ts that represents the "populated"
 * version of a Product2 document.
 * */

export const Product2Schema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string().optional(),
  attributes: z.array(Product2AttributeSchema),
  vendor: z.string(),
  team: z.object({ _id: zExt.objectId(), name: z.string() }),
  consigner: zExt.objectId().nullish(),
  hsCode: z.string().optional(),
  externalId: z.string().optional(),
  externalSource: z.string().optional(),
  images: z.array(Product2ImageSchema),
  status: Product2StatusSchema.default("ACTIVE"),
  variants: z.array(Product2VariantSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const Product2Schema: z.ZodType<Product2> = Product2Schema_;
export interface Product2 extends z.infer<typeof Product2Schema_> {}

export const Product2UpsertSchema_ = Product2Schema_.extend({
  _id: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});
export const Product2UpsertSchema: z.ZodType<Product2Upsert> =
  Product2UpsertSchema_;
export type Product2Upsert = z.infer<typeof Product2UpsertSchema_>;

export const NormalizedProduct2Schema = Product2Schema_.extend({
  attributes: z.array(zExt.objectId()),
});

export type NormalizedProduct2 = z.infer<typeof NormalizedProduct2Schema>;

/* InventoryLevel */
export const InventoryLevelSchema = z.object({
  warehouse: zExt.objectId(),
  quantity: z.number().min(0),
  committed: z.number().min(0).default(0),
});

/* InventoryItem */
export type InventoryItem = z.infer<typeof InventoryItemSchema>;

export const InventoryItemSchema = z.object({
  _id: zExt.objectId(),
  variant: zExt.objectId(),
  inventoryLevels: z.array(InventoryLevelSchema),
});

const InventoryItemUpsertSchema_ = InventoryItemSchema.extend({
  _id: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const InventoryItemUpsertSchema: z.ZodType<InventoryItemUpsert> =
  InventoryItemUpsertSchema_;
export type InventoryItemUpsert = z.infer<typeof InventoryItemUpsertSchema_>;

/* CatalogProductSchema */
export const CatalogVariantSchema = z.object({
  _id: zExt.objectId(),
  attributeValues: z.array(Product2AttributeValueSchema),
  images: z.array(Product2ImageSchema),
  lines: z.array(z.object({ variant: zExt.objectId(), quantity: z.number() })),
});

export const CatalogProductSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  externalSource: z.string().optional(),
  attributeValues: z.array(Product2AttributeValueSchema),
  images: z.array(Product2ImageSchema),
  team: zExt.objectId(),
  variants: z.array(CatalogVariantSchema),
});

/**
 * Populated Schemas
 *
 * TODO: A lot of these schemas are rpc-specific and should be moved to the rpc's that use them.
 * */

/* VariantWithQuantity */
export const VariantWithQuantitySchema = Product2VariantSchema.extend({
  quantity: z.number().min(0),
});

export type VariantWithQuantity = z.infer<typeof VariantWithQuantitySchema>;

/* InventoryItemWithWarehouse */
export const InventoryLevelWithWarehouseSchema = InventoryLevelSchema.extend({
  warehouse: WarehouseSchema,
});

export type InventoryLevelWithWarehouse = z.infer<
  typeof InventoryLevelWithWarehouseSchema
>;

export const InventoryItemWithWarehouseSchema = InventoryItemSchema.extend({
  inventoryLevels: z.array(InventoryLevelWithWarehouseSchema),
});

export type InventoryItemWithWarehouse = z.infer<
  typeof InventoryItemWithWarehouseSchema
>;

/* ProductWithInventoryLevels */
export const VariantWithInventoryLevelsSchema = Product2VariantSchema.extend({
  inventoryLevels: z.array(InventoryLevelWithWarehouseSchema),
});

export const ProductWithInventoryLevelsSchema = Product2Schema_.extend({
  variants: z.array(VariantWithInventoryLevelsSchema),
});

export type ProductWithInventoryLevels = z.infer<
  typeof ProductWithInventoryLevelsSchema
>;

/* ProductInventory */
export const ProductInventorySchema = Product2Schema_.extend({
  variants: z.array(VariantWithQuantitySchema),
});

export type ProductInventory = z.infer<typeof ProductInventorySchema>;

export const ProductWithInventorySchema = NormalizedProduct2Schema.extend({
  totalQuantity: z.number(),
  warehouseName: z.string().optional(),
});

export type ProductWithInventory = z.infer<typeof ProductWithInventorySchema>;

export const ShipmentProductSchema = z.object({
  images: z.array(z.string()).optional().nullable(),
  quantity: z.number(),
  reasons: z.array(z.string()).optional().nullable(),
  price: z.number(),
  option1: z.string().optional().nullable(),
  option2: z.string().optional().nullable(),
  option3: z.string().optional().nullable(),
  variant_title: z.string(),
  product_title: z.string(),
  product_id: z.string().optional(),
  variant_id: z.string().optional(),
  sku: z.string(),
  barcode: z.string().optional().nullable(),
  isFinalSaleReturn: z.boolean().optional().nullable(),
});

export type ShipmentProduct = z.infer<typeof ShipmentProductSchema>;

export type BarcodeData = {
  title: string;
  sku: string;
  price?: string;
  barcode: string;
};
